import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { signOut } from "@/contexts/AuthContext";
import { useRefreshToken } from "@/hooks/useRefreshToken";
import { v1 as uuidv1 } from "uuid";
import jwtDecode from "jwt-decode";

interface DecodedToken {
  exp: number;
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp - currentTime < 60;
  } catch {
    return true;
  }
}

const bankingApi = axios.create({
  baseURL: `${import.meta.env.VITE_API_BANKING_URL}`,
  headers: {
    Authorization: `Bearer ${localStorage.getItem("barte-auth-token")}`,
    "access-control-allow-origin": "*",
  },
});

bankingApi.interceptors.request.use(
  async (config) => {
    const currentToken = localStorage.getItem("barte-auth-token");
    const refreshTokenStorage = localStorage.getItem("barte-refresh-token");
    if (currentToken && isTokenExpired(currentToken)) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const response = await fetch(
            `${import.meta.env.VITE_API_URL}public/auth/refresh-token`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                refreshToken: String(refreshTokenStorage),
              }),
            }
          );
          const data = await response.json();
          const {
            email,
            roles,
            token: newToken,
            idSeller,
            document,
            refreshToken,
          } = data;
          localStorage.setItem("barte-auth-token", newToken);
          localStorage.setItem(
            "barte-user-info",
            JSON.stringify({ email, roles, idSeller, document })
          );
          localStorage.setItem("barte-refresh-token", refreshToken);
          config.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
        } catch (error) {
          processQueue(error, null);
          signOut(true);
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            config.headers.Authorization = `Bearer ${token}`;
            return config;
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
    }

    const uuid = uuidv1();
    config.headers.post["x-idempotency-key"] = uuid;
    return config;
  },
  (error) => Promise.reject(error)
);

bankingApi.interceptors.response.use(
  (response) => {
    return Promise.resolve(response);
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    if (error?.response?.status === 401 && !originalRequest?._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return bankingApi(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshTokenStorage = localStorage.getItem("barte-refresh-token");
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}public/auth/refresh-token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refreshToken: String(refreshTokenStorage),
            }),
          }
        );
        const data = await response.json();
        const {
          email,
          roles,
          token: newToken,
          idSeller,
          document,
          refreshToken,
        } = data;
        localStorage.setItem("barte-auth-token", newToken);
        localStorage.setItem(
          "barte-user-info",
          JSON.stringify({ email, roles, idSeller, document })
        );
        localStorage.setItem("barte-refresh-token", refreshToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return bankingApi(originalRequest);
      } catch (error) {
        processQueue(error, null);
        signOut(true);
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    if (error?.response?.status === 403) {
      signOut();
    }
    return Promise.reject(error);
  }
);

export const Api_Banking = bankingApi;
